import {router, useForm} from '@inertiajs/vue3';
import {Errors, Page, PageProps, VisitOptions} from '@inertiajs/core';
import {FormDataConvertible} from '@inertiajs/core/types/types';

type RequestType = 'load' | 'create' | 'update' | 'delete';

const getRequestOptions = <ExpectedResponse extends PageProps>(
	action: RequestType,
	reload: string[],
	resolve: (page: Page<ExpectedResponse>) => void,
	reject: (error: Errors) => void
): VisitOptions => {
	const options: VisitOptions = {
		preserveState: true,
		preserveScroll: true,
		only: reload,
		onSuccess: (page: Page<ExpectedResponse>) => {
			resolve(page);
		},
		onError: (e) => {
			reject(e);
		},
	};

	if (action === 'load') {
		delete options['preserveState'];
		delete options['preserveScroll'];
	}

	return options;
};

export const sendReloadRequest = <ExpectedResponse extends PageProps>(
	reload: (keyof ExpectedResponse)[]
): Promise<Page<ExpectedResponse>> => {
	return new Promise<Page<ExpectedResponse>>((resolve, reject) => {
		router.reload(
			getRequestOptions('load', reload as string[], resolve, reject)
		);
	});
};

export const sendCreateRequest = <ExpectedResponse extends PageProps>(
	targetRoute: string,
	request: Record<string, FormDataConvertible>,
	reload: (keyof ExpectedResponse)[]
): Promise<Page<ExpectedResponse>> => {
	return new Promise((resolve, reject) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		useForm(request as any).post(
			targetRoute,
			getRequestOptions('create', reload as string[], resolve, reject)
		);
	});
};

export const sendUpdateRequest = <ExpectedResponse extends PageProps>(
	targetRoute: string,
	request: Record<string, FormDataConvertible>,
	reload: (keyof ExpectedResponse)[]
): Promise<Page<ExpectedResponse>> => {
	return new Promise((resolve, reject) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		useForm(request as any).put(
			targetRoute,
			getRequestOptions('update', reload as string[], resolve, reject)
		);
	});
};

export const sendDeleteRequest = <ExpectedResponse extends PageProps>(
	targetRoute: string,
	reload: (keyof ExpectedResponse)[]
): Promise<Page<ExpectedResponse>> => {
	return new Promise((resolve, reject) => {
		router.delete(
			targetRoute,
			getRequestOptions('delete', reload as string[], resolve, reject)
		);
	});
};
